/**
 * Orders Store (Zustand)
 * Manages vendor order state
 */

import { create } from 'zustand';
import { Order, OrderStatus } from '@/lib/constants';
import { ordersApi } from '@/lib/api';
import { logger } from '@/lib/logger';

interface OrdersState {
  newOrders: Order[];
  activeOrders: Order[];
  completedOrders: Order[];
  isLoading: boolean;

  // Actions
  fetchOrders: () => Promise<void>;
  addNewOrder: (order: Order) => void;
  updateOrderStatus: (orderId: string, status: OrderStatus) => void;
  acceptOrder: (orderId: string, estimatedReadyTime: number) => Promise<void>;
  rejectOrder: (orderId: string, reason: string) => Promise<void>;
  markOrderStatus: (orderId: string, status: OrderStatus) => Promise<void>;
  removeOrder: (orderId: string) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

export const useOrdersStore = create<OrdersState>((set, get) => ({
  newOrders: [],
  activeOrders: [],
  completedOrders: [],
  isLoading: false,

  fetchOrders: async () => {
    try {
      set({ isLoading: true });
      const orders = await ordersApi.getOrders();

      const newOrders = orders.filter((o: Order) => o.status === 'pending');
      const activeOrders = orders.filter((o: Order) =>
        ['accepted', 'preparing', 'ready_for_pickup'].includes(o.status)
      );
      const completedOrders = orders.filter((o: Order) =>
        ['picked_up', 'out_for_delivery', 'delivered', 'cancelled'].includes(o.status)
      );

      set({ newOrders, activeOrders, completedOrders, isLoading: false });
    } catch (error) {
      logger.error('Failed to fetch orders', error as Error);
      set({ isLoading: false });
    }
  },

  addNewOrder: (order) => {
    set((state) => ({
      newOrders: [order, ...state.newOrders],
    }));
  },

  updateOrderStatus: (orderId, status) => {
    set((state) => {
      // Find order in all lists
      let order: Order | undefined;

      order = state.newOrders.find((o) => o.id === orderId);
      if (!order) order = state.activeOrders.find((o) => o.id === orderId);
      if (!order) order = state.completedOrders.find((o) => o.id === orderId);

      if (!order) {
        logger.warn('Order not found for status update', { orderId, status });
        return state;
      }

      // Update order status
      const updatedOrder = { ...order, status, updatedAt: new Date().toISOString() };

      // Move to appropriate list
      const newOrders = state.newOrders.filter((o) => o.id !== orderId);
      const activeOrders = state.activeOrders.filter((o) => o.id !== orderId);
      const completedOrders = state.completedOrders.filter((o) => o.id !== orderId);

      if (status === 'pending') {
        newOrders.unshift(updatedOrder);
      } else if (['accepted', 'preparing', 'ready_for_pickup'].includes(status)) {
        activeOrders.unshift(updatedOrder);
      } else {
        completedOrders.unshift(updatedOrder);
      }

      return { newOrders, activeOrders, completedOrders };
    });
  },

  acceptOrder: async (orderId, estimatedReadyTime) => {
    try {
      await ordersApi.acceptOrder(orderId, estimatedReadyTime);
      get().updateOrderStatus(orderId, 'accepted');
      logger.info('Order accepted', { orderId });
    } catch (error) {
      logger.error('Failed to accept order', error as Error, { orderId });
      throw error;
    }
  },

  rejectOrder: async (orderId, reason) => {
    try {
      await ordersApi.rejectOrder(orderId, reason);
      get().removeOrder(orderId);
      logger.info('Order rejected', { orderId, reason });
    } catch (error) {
      logger.error('Failed to reject order', error as Error, { orderId });
      throw error;
    }
  },

  markOrderStatus: async (orderId, status) => {
    try {
      await ordersApi.updateStatus(orderId, status);
      get().updateOrderStatus(orderId, status);
      logger.info('Order status updated', { orderId, status });
    } catch (error) {
      logger.error('Failed to update order status', error as Error, { orderId, status });
      throw error;
    }
  },

  removeOrder: (orderId) => {
    set((state) => ({
      newOrders: state.newOrders.filter((o) => o.id !== orderId),
      activeOrders: state.activeOrders.filter((o) => o.id !== orderId),
      completedOrders: state.completedOrders.filter((o) => o.id !== orderId),
    }));
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  reset: () => {
    set({
      newOrders: [],
      activeOrders: [],
      completedOrders: [],
      isLoading: false,
    });
  },
}));

